import type { LinkTarget } from "../types";

export type LinkValue = LinkTarget | string | URL | null | undefined;

/** Resolves a link value (slug, external, URL) into a usable href. */
export function resolveLinkHref(target: LinkValue): string | undefined {
  if (!target) return undefined;

  if (typeof target === "string" || target instanceof URL) {
    const normalized = target.toString().trim();
    return normalized.length ? normalized : undefined;
  }

  if (typeof target === "object") {
    const external = target.external?.toString().trim();
    if (external) {
      return external;
    }

    const slug = target.slug?.toString().trim();
    if (slug) {
      return slug.startsWith("/") ? slug : `/${slug}`;
    }

    // pageId is reserved for future slug syncing; intentionally ignored for now.
  }

  return undefined;
}

/** Checks whether the link points to an external URL (http/https). */
export function isExternalHref(target: LinkValue): boolean {
  const href = resolveLinkHref(target);
  return href ? /^https?:\/\//i.test(href) : false;
}

/**
 * Treats placeholder destinations as "not configured". A link is unconfigured when it
 * resolves to nothing, to the bare home page ("/"), or to an empty anchor ("#", "#top").
 * The CMS uses those exact values for buttons whose real page isn't set up yet (e.g.
 * "Emploi" → "#", "Musée" → "/"), and a genuine nav/footer item never legitimately points
 * at the bare homepage — the logo already does that. Used by the dev-placeholder layer so
 * such links open the preview modal and are hidden entirely in production.
 */
export function isUnconfiguredHref(target: LinkValue): boolean {
  const href = resolveLinkHref(target);
  if (!href) return true;
  const trimmed = href.trim();
  return trimmed === "/" || trimmed.startsWith("#");
}

/** A usable destination href, or undefined when the link is unconfigured (see {@link isUnconfiguredHref}). */
export function resolveConfiguredHref(target: LinkValue): string | undefined {
  return isUnconfiguredHref(target) ? undefined : resolveLinkHref(target);
}

/**
 * Canonical membership key for an internal href: percent-decoded, query/hash stripped,
 * no leading/trailing slashes, collapsed doubles, lower-cased. MUST stay the exact mirror
 * of `normalizeRequestPath` (BCC.PUBLIC.Nextjs/src/lib/redirects.ts) — the liveness set is
 * built with that normalizer, so a drift silently breaks every membership test (locked by
 * the links-normalize-parity vitest).
 */
export function normalizeHrefKey(href: string): string {
  let s = (href ?? "").trim();
  const cut = s.search(/[?#]/);
  if (cut >= 0) s = s.slice(0, cut);
  try {
    s = decodeURIComponent(s);
  } catch {
    // Malformed escape sequence — fall back to the raw value.
  }
  s = s.replace(/^\/+|\/+$/g, "");
  s = s.replace(/\/{2,}/g, "/");
  return s.toLowerCase();
}

/**
 * Should this href be subjected to the liveness test at all? Only internal page paths
 * qualify. Everything else is treated as ALIVE (fail-open): external http(s), mailto:/tel:,
 * anchors, and any path whose last segment carries a file extension — storage assets
 * (PDF, images) are served from the bucket and never appear in the sitemap, so testing
 * them would hide living documents.
 */
export function isLivenessCheckableHref(href: string): boolean {
  const trimmed = (href ?? "").trim();
  if (!trimmed.startsWith("/")) return false;
  const key = normalizeHrefKey(trimmed);
  if (!key) return false;
  const lastSegment = key.split("/").pop() ?? "";
  return !/\.[a-z0-9]{2,5}$/.test(lastSegment);
}


